import { CapacitorHttp } from '@capacitor/core';

import { APP_MEDIA_PATH_PREFIX, APP_QUERY_PARAMS, MEDIA_CDN_HOSTNAME } from '../../../common/utils/urlConstants';
import { parseUrl } from '../../../common/utils/urlUtil';
import { MimeType } from '../../../common/media/mimeConstants';
import fetchImageDataFromUrl from '../../components/images/imageColorExtraction/fetchImageDataFromUrl';
import platformSingleton from '../../platform/platformSingleton';
import { isPlatformCapacitor } from '../../platform/utils/platformDetailsUtils';
import { getCurrentUserToken } from '../../auth/authService';
import { getAuthenticatedHeaders, shouldFetchWithAuthentication } from '../../auth/authUtils';
import { getMimeTypeFromImageUrl } from './imageUtils';

/**
 * Fetch image bytes through the browser fetch API when standard web CORS/auth handling is available.
 */
const fetchImageResponse = (url: string, token?: string): Promise<Response> =>
    fetch(url, {
        headers: getAuthenticatedHeaders(token),
    });

/**
 * App-hosted v2 media URLs require element-scoped permission checks, so the first browser request must be authenticated.
 * CDN-hosted media stays public-first to avoid the CORS regression fixed in PR #20897.
 */
const shouldFetchWithAuthenticationImmediately = (url: string): boolean => {
    const parsedUrl = parseUrl(url);
    if (!parsedUrl) return false;

    const isAppMediaUrl =
        parsedUrl.hostname !== MEDIA_CDN_HOSTNAME && parsedUrl.pathname.startsWith(APP_MEDIA_PATH_PREFIX);
    if (!isAppMediaUrl) return false;

    return (
        parsedUrl.searchParams.get(APP_QUERY_PARAMS.VERSION) === '2' &&
        !!parsedUrl.searchParams.get(APP_QUERY_PARAMS.ELEMENT_ID)
    );
};

/**
 * Download image bytes through the Capacitor native HTTP layer, which bypasses the webview's CORS
 * restrictions on the native apps.
 */
const fetchImageBlobUsingCapacitorHTTP = async (imageUrl: string, imageType?: string): Promise<Blob> => {
    const token = getCurrentUserToken();
    const shouldSendAuthentication = !!token && shouldFetchWithAuthentication(imageUrl);
    const response = await CapacitorHttp.get({
        url: imageUrl,
        responseType: 'arraybuffer',
        headers: getAuthenticatedHeaders(shouldSendAuthentication ? token : undefined),
    });

    if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to fetch image (${response.status})`);
    }

    const base64Data = response.data;
    const byteCharacters = atob(base64Data);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
    }

    const responseMimeType = response.headers?.['content-type'];
    const resolvedImageType = imageType || responseMimeType || getMimeTypeFromImageUrl(imageUrl, MimeType.JPEG);

    return new Blob([byteArray], { type: resolvedImageType });
};

/**
 * Download image bytes for editing or background removal.
 * Try public image URLs without auth first, except for app-hosted v2 media URLs that require auth for permission checks.
 * On the native Capacitor apps the browser fetch can fail CORS checks (the webview origin isn't allowed
 * by the media hosts), so fall back to the native HTTP layer there.
 */
export const fetchImageBlobFromUrl = async (url: string, imageType?: string): Promise<Blob> => {
    try {
        const token = getCurrentUserToken();
        const canSendAuthentication = !!token && shouldFetchWithAuthentication(url);
        const shouldSendAuthenticationImmediately =
            canSendAuthentication && shouldFetchWithAuthenticationImmediately(url);
        const response = await fetchImageResponse(url, shouldSendAuthenticationImmediately ? token : undefined);

        if (response.ok) return response.blob();
        if (!canSendAuthentication || shouldSendAuthenticationImmediately) {
            throw new Error(`Failed to fetch image (${response.status})`);
        }

        const authenticatedResponse = await fetchImageResponse(url, token);

        if (authenticatedResponse.ok) return authenticatedResponse.blob();

        throw new Error(`Failed to fetch image (${authenticatedResponse.status})`);
    } catch (error) {
        // Fetch-level failures (CORS/network) reject with a TypeError. Explicit HTTP status errors
        // reached the server, so retrying through the native layer would just fail the same way.
        const isFetchLevelFailure = error instanceof TypeError;
        if (!isFetchLevelFailure || !isPlatformCapacitor(platformSingleton)) throw error;

        return fetchImageBlobUsingCapacitorHTTP(url, imageType);
    }
};

/**
 * Download image bytes through Capacitor for editor flows that need a local object URL on mobile.
 */
export const fetchImageUsingCapacitorHTTP = async (
    imageUrl: string,
    imageType?: string,
): Promise<{ objectUrl: string }> => {
    const blob = await fetchImageBlobFromUrl(imageUrl, imageType);
    return { objectUrl: URL.createObjectURL(blob) };
};

// Fetch image data using through capacitor for flows that require the image data directly
export const fetchImageDataUsingCapacitorHTTP = async (imageUrl: string, imageType?: string): Promise<ImageData> => {
    const { objectUrl } = await fetchImageUsingCapacitorHTTP(imageUrl, imageType);

    try {
        return await fetchImageDataFromUrl(objectUrl);
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};
